import { Button, Icon16Alert, Typography } from "@cur8/maneki";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "@pomle/react-router-paths";
import { useAssignToEcgMutation } from "hooks/mutations/useAssignToEcgMutation";
import { useECGDeviceQuery } from "hooks/queries/useECGDeviceQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { paths } from "render/routes/paths";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { List } from "render/ui/presentation/List";
import styles from "./styles.module.sass";
import listStyles from "../CheckInCompleteView/styles.module.sass";
import { ReactNode, useEffect, useMemo } from "react";
import { APITypesV1 } from "@cur8/api-client";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { readScanStateFromVisit } from "lib/readScanStateFromVisit";
import { formatAddressableName } from "lib/formatMemberName";

interface State {
  label: ReactNode;
  subLabel: ReactNode;
  deviceStatusLabel: string;
  deviceStatusHighlight?: "error";
  cta: {
    submit?: { label: ReactNode; onSubmit: () => void };
    cancel?: ReactNode;
  };
}

export function AssignToNoravECGDevice({
  patientId,
  visitId,
  goTo,
  returnPath,
}: {
  patientId: string;
  visitId: string;
  goTo(url: string): void;
  returnPath?: string;
}) {
  const resolvedReturnPath =
    returnPath ?? paths.checkInSuccess.url({ patientId, visitId });
  const location = useLocation();
  // Entry-point views (CheckInCompleteView, FollowUpEcgView) fire the happy-path
  // assign mutation directly and navigate here with ?assignError=1 when it
  // fails, so the error state is rendered on this page even though the local
  // submitHandler hasn't been triggered yet.
  const hasUpstreamAssignError =
    location.searchParams.get("assignError") === "1";
  const patientQuery = usePatientQuery({ patientId });
  const eCGDeviceQuery = useECGDeviceQuery({ patientId, visitId });
  // Only the follow-up flow needs the visit's scan state (to keep the
  // confirmation rendering even when Norav reports CanAssign). The base-scan
  // flow handles CanAssign upstream and only renders re-assign confirmations
  // for AssignedToMember/OtherMemberAssigned, so skip the fetch entirely
  // there.
  const visitQuery = useVisitQuery({
    patientId,
    visitId,
    options: { enabled: returnPath != null },
  });
  const assignToEcgMutation = useAssignToEcgMutation();
  const submitHandler = useMutation({
    mutationFn: () => assignToEcgMutation.mutateAsync({ patientId, visitId }),
    onSuccess: async () => {
      await eCGDeviceQuery.refetch();
      goTo(resolvedReturnPath);
    },
  });

  const name = formatAddressableName(patientQuery.data?.name);
  const showAssignError = submitHandler.isError || hasUpstreamAssignError;
  const ecgScanState = readScanStateFromVisit("ecg", visitQuery.data);
  const hasScanProgressed =
    ecgScanState === APITypesV1.ImmutableScanState.Inprogress ||
    ecgScanState === APITypesV1.ImmutableScanState.Finished;

  // CanAssign with no recorded scan is handled upstream by entry-point views
  // that fire the mutation directly. If we land here in that state (stale
  // query, deep link), redirect back rather than render a confirmation. When
  // a scan has already started, Norav's device lock can have expired even
  // though the recording happened — render a confirmation instead.
  const isCanAssignFallback =
    !showAssignError &&
    !eCGDeviceQuery.error &&
    !hasScanProgressed &&
    eCGDeviceQuery.data?.assignmentStatus ===
      APITypesV1.DeviceAssignmentStatus.CanAssign;

  useEffect(() => {
    if (isCanAssignFallback) {
      goTo(resolvedReturnPath);
    }
  }, [isCanAssignFallback, goTo, resolvedReturnPath]);

  const state = useMemo<State | undefined>(() => {
    if (showAssignError) {
      return {
        label: "Couldn’t assign member to the ECG",
        subLabel:
          "Please try again, or cancel and proceed with the other examinations.",
        deviceStatusLabel: "Device unavailable",
        deviceStatusHighlight: "error",
        cta: {
          submit: {
            label: "Try again",
            onSubmit: () => submitHandler.mutate(),
          },
          cancel: "Cancel",
        },
      };
    }
    if (eCGDeviceQuery.error) {
      return {
        label: "Couldn’t connect to ECG device",
        subLabel: "Please check the device, or contact support",
        deviceStatusLabel: "Device unavailable",
        deviceStatusHighlight: "error",
        cta: {
          submit: {
            label: "Try again",
            onSubmit: () => {
              eCGDeviceQuery.refetch();
            },
          },
          cancel: "Cancel",
        },
      };
    }
    const submitCta = {
      submit: {
        label: "Re-assign to ECG",
        onSubmit: () => submitHandler.mutate(),
      },
      cancel: "Cancel" as ReactNode,
    };

    // Visit scan state describes what THIS member's ECG has done; prefer it
    // over the device's assignmentStatus, which Norav can flip independently
    // (lock expiry, dev-double cycling) and would otherwise drive misleading
    // copy like "recording in progress" on a finished scan.
    if (ecgScanState === APITypesV1.ImmutableScanState.Finished) {
      return {
        label: `Are you sure you want to re-assign ${name} to the ECG?`,
        subLabel: `${name} already had a 12-lead ECG today.`,
        deviceStatusLabel: "Free",
        deviceStatusHighlight: undefined,
        cta: submitCta,
      };
    }
    if (ecgScanState === APITypesV1.ImmutableScanState.Inprogress) {
      return {
        label: `Are you sure you want to re-assign ${name} to the ECG?`,
        subLabel: "A recording is currently in progress.",
        deviceStatusLabel: "In progress",
        deviceStatusHighlight: undefined,
        cta: submitCta,
      };
    }
    switch (eCGDeviceQuery.data?.assignmentStatus) {
      case APITypesV1.DeviceAssignmentStatus.AssignedToMember:
        return {
          label: `Are you sure you want to re-assign ${name} to the ECG?`,
          subLabel: `${name} already had a 12-lead ECG today.`,
          deviceStatusLabel: "Free",
          deviceStatusHighlight: undefined,
          cta: submitCta,
        };
      case APITypesV1.DeviceAssignmentStatus.OtherMemberAssigned:
        return {
          label: `Are you sure you want to re-assign ${name} to the ECG?`,
          subLabel: "A recording is currently in progress.",
          deviceStatusLabel: "In progress",
          deviceStatusHighlight: undefined,
          cta: submitCta,
        };
    }
    return undefined;
  }, [showAssignError, submitHandler, eCGDeviceQuery, name, ecgScanState]);

  if (state == null || isCanAssignFallback) {
    return null;
  }

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <div className={styles.content}>
            <section className={styles.title}>
              <Typography variant="display-s">{state.label}</Typography>
              <Typography variant="body-m">{state.subLabel}</Typography>
            </section>
            <section>
              <List>
                <List.Item>
                  <div className={listStyles.scanItem}>
                    <div className={listStyles.itemTitle}>
                      <Typography variant="title-s">ECG (12-lead)</Typography>
                    </div>
                    <DeviceStatusBadge
                      label={state.deviceStatusLabel}
                      highlight={state.deviceStatusHighlight}
                    />
                  </div>
                </List.Item>
              </List>
            </section>
            <section className={styles.cta}>
              {state.cta.cancel ? (
                <Button
                  fillContainer
                  variant="secondary"
                  onClick={() => goTo(resolvedReturnPath)}
                >
                  {state.cta.cancel}
                </Button>
              ) : undefined}
              {state.cta.submit ? (
                <Button
                  fillContainer
                  state={
                    submitHandler.isPending || submitHandler.isSuccess
                      ? "loading"
                      : undefined
                  }
                  onClick={state.cta.submit.onSubmit}
                >
                  {state.cta.submit.label}
                </Button>
              ) : undefined}
            </section>
          </div>
        }
      />
    </PageFrameContent>
  );
}

function DeviceStatusBadge({
  label,
  highlight,
}: {
  label: string;
  highlight?: "error";
}) {
  return (
    <div className={listStyles.scanStatusBadge} data-highlight={highlight}>
      {highlight === "error" && <Icon16Alert />}
      <Typography variant="eyebrow-m" as="span">
        {label}
      </Typography>
    </div>
  );
}
